import { useState, useEffect } from 'react';
import { MapPin, X, Pencil, MapPinned } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox/checkbox';
import { flyToAndShowPin, showMarker, removeMarker, geocodeAddress } from '@/pages/mapview/MapCommon';
import { mapbox_accessToken, getAddressFromCoord } from '@/lib';
import { Input, Label, Textarea, Combobox, Button } from '@/components';
import { useERSContext } from '@/context';

const SEVERITY_OPTIONS = [
  { value: 4, label: 'Critical', dotColor: 'bg-icon-error' },
  { value: 3, label: 'High', dotColor: 'bg-chart-2' },
  { value: 2, label: 'Medium', dotColor: 'bg-chart-4' },
  { value: 1, label: 'Low', dotColor: 'bg-interactive' },
  { value: 0, label: 'Minimal', dotColor: 'bg-icon-muted' },
];

export default function POIItem({
  poi,
  checkedPoiIds,
  setCheckedPoiIds,
  editablePoiIds,
  setEditablePoiIds,
  map,
  markersObj,
  POIs,
  setPOIs,
}) {
  const { postMessageToPopMapWindow, editingMarkerFromPopMap } = useERSContext();
  const isEditing = editablePoiIds.includes(poi.id);
  const [editingPOI, setEditingPOI] = useState(JSON.parse(JSON.stringify(poi)));

  async function updatePOI() {
    const { updatedAt, updatedBy, isNew, ...otherOriginal } = poi;
    const newPOI = {
      displayName: editingPOI.displayName,
      description: editingPOI.description || '',
      symbol: editingPOI.symbol || 'building',
      location: editingPOI.location,
      severity: parseInt(editingPOI.severity) || 1,
      color: editingPOI.color || '#000000',
      updatedAt: Math.floor(Date.now() / 1000),
    };
    if (poi.isNew) {
      setPOIs(prev => prev.filter(p => p.id !== poi.id));
      delete newPOI.updatedAt;
      delete otherOriginal.id;
      try {
        await wbSdk.mapsService.createPOI({ ...otherOriginal, ...newPOI });
      } catch (err) {
        console.error('Failed to create poi', err);
      }
    } else {
      setPOIs(prev => prev.map(a => (a.id === poi.id ? { ...a, ...newPOI } : a)));
      setCheckedPoiIds(prev => (prev.includes(poi.id) ? prev : [...prev, poi.id]));
      try {
        await wbSdk.mapsService.editPOI({ ...otherOriginal, ...newPOI });
      } catch (err) {
        console.error('Failed to update poi', err);
      }
    }
    setEditablePoiIds([]);
  }

  async function deletePOI() {
    try {
      await wbSdk.mapsService.deletePOI(editingPOI.id);
    } catch (err) {
      console.error('Failed to delete poi', err);
    }
    setEditablePoiIds([]);
  }

  function updateEditingPOILoc(editingObj) {
    showMarker({
      category: 'editingPoi',
      item: editingObj,
      map,
      markersObj,
      afterDragFunc: async ({ lat, lng }) => {
        const addressName = await getAddressFromCoord(lat, lng, mapbox_accessToken);
        setEditingPOI(prev => ({
          ...prev,
          location: [lng, lat],
          address: addressName,
        }));
      },
      funcPostToPopMap: postMessageToPopMapWindow,
    });
  }

  useEffect(() => {
    if (!isEditing) {
      const fetchReverseGeocode = async () => {
        if (editingPOI.location?.[0] && editingPOI.location?.[1]) {
          try {
            const result = await getAddressFromCoord(
              editingPOI.location[1],
              editingPOI.location[0],
              mapbox_accessToken
            );
            setEditingPOI(prev => ({ ...prev, address: result || '' }));
          } catch (e) {
            console.warn('Reverse geocode failed', e);
          }
        }
      };
      fetchReverseGeocode();
    }

    if (isEditing) {
      updateEditingPOILoc(editingPOI);
    } else {
      removeMarker({ category: 'editingPoi', id: poi.id, markersObj, funcPostToPopMap: postMessageToPopMapWindow });
    }
  }, [editingPOI.location, isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    if (editingMarkerFromPopMap.category !== 'editingPoi' || editingPOI?.id !== editingMarkerFromPopMap?.item?.id)
      return;
    const updateloc = async (newLat, newLng) => {
      const addressName = await getAddressFromCoord(newLat, newLng, mapbox_accessToken);
      setEditingPOI(prev => ({
        ...prev,
        location: [newLng, newLat],
        address: addressName,
      }));
    };
    updateloc(editingMarkerFromPopMap?.item?.location[1], editingMarkerFromPopMap?.item?.location[0]);
  }, [editingMarkerFromPopMap]);

  useEffect(() => {
    if (checkedPoiIds.includes(poi.id)) {
      showMarker({ category: 'poi', item: poi, map, markersObj, funcPostToPopMap: postMessageToPopMapWindow });
    } else {
      removeMarker({ category: 'poi', id: poi.id, markersObj, funcPostToPopMap: postMessageToPopMapWindow });
    }
  }, [checkedPoiIds, poi]);

  useEffect(() => {
    return () => {
      const stillExists = POIs.some(p => p.id === poi.id);
      if (!stillExists) {
        removeMarker({ category: 'poi', id: poi.id, markersObj, funcPostToPopMap: postMessageToPopMapWindow });
      }
    };
  }, [POIs, poi.id]);

  useEffect(() => {
    return () => {
      removeMarker?.({ category: 'editingPoi', id: poi.id, markersObj, funcPostToPopMap: postMessageToPopMapWindow });
    };
  }, []);

  function enableEdit() {
    if (editingPOI.location?.[0] && map) {
      flyToAndShowPin(editingPOI.location[1], editingPOI.location[0], 17, map, postMessageToPopMapWindow);
    }
    setEditablePoiIds([poi.id]);
    setCheckedPoiIds(prev => prev.filter(id => id !== poi.id));
    removeMarker({ category: 'poi', id: poi.id, markersObj, funcPostToPopMap: postMessageToPopMapWindow });
  }

  useEffect(() => {
    if (poi.isNew) enableEdit();
  }, [poi.id]);

  const handleCancel = () => {
    setEditablePoiIds([]);
    if (poi.isNew) {
      setPOIs(prev => prev.filter(item => item.id !== poi.id));
    }
  };

  const flyTo = () => {
    if (poi.location?.[0] && map) {
      const currentZoom = map.getZoom();
      const targetZoom = currentZoom < 14 ? 14 : currentZoom;
      flyToAndShowPin(poi.location[1], poi.location[0], targetZoom, map, postMessageToPopMapWindow);
    }
  };

  const handleAddressSearch = async value => {
    try {
      const result = await geocodeAddress(value, mapbox_accessToken);
      if (result) {
        const newLoc = [result.lng, result.lat];
        setEditingPOI(prev => ({
          ...prev,
          location: newLoc,
          address: result.display_name,
        }));
      }
    } catch (e) {
      console.warn('Geocode failed', e);
    }
  };

  const severityOption = SEVERITY_OPTIONS.find(s => s.value === (poi.severity ?? 1));

  // Edit Mode
  if (isEditing) {
    return (
      <div className="flex flex-col rounded-xl border border-border bg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between bg-subtle border-b border-border px-3 py-2">
          <div className="flex items-center gap-2">
            <MapPinned size={20} className="text-muted" />
            <span className="font-medium text-sm">{poi.isNew ? 'Create POI' : 'Edit POI'}</span>
          </div>
          <button onClick={handleCancel} className="p-1 hover:bg-strong rounded transition-colors" aria-label="Close">
            <X size={18} className="text-muted" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-3 p-3">
          {/* POI Name */}
          <div className="flex flex-col gap-1">
            <Label className="text-sm">
              POI Name <span className="text-error">*</span>
            </Label>
            <Input
              value={editingPOI.displayName}
              onChange={e => setEditingPOI(prev => ({ ...prev, displayName: e.target.value }))}
              placeholder="Enter POI Name"
            />
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1">
            <Label className="text-sm">Description</Label>
            <Textarea
              value={editingPOI.description || ''}
              onChange={e => setEditingPOI(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Description of the POI"
              className="min-h-[60px]"
            />
          </div>

          {/* Severity */}
          <div className="flex flex-col gap-1">
            <Label className="text-sm">
              Severity <span className="text-error">*</span>
            </Label>
            <Combobox
              data={SEVERITY_OPTIONS}
              placeholder="Select Severity"
              selectedData={SEVERITY_OPTIONS.find(s => s.value === editingPOI.severity)}
              onChange={item => setEditingPOI(prev => ({ ...prev, severity: item?.value ?? 1 }))}
            />
          </div>

          {/* Location */}
          <div className="flex flex-col gap-1">
            <Label className="text-sm">Location</Label>
            <Input
              value={editingPOI.address || ''}
              onChange={e => setEditingPOI(prev => ({ ...prev, address: e.target.value }))}
              onBlur={e => handleAddressSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleAddressSearch(e.target.value);
                }
              }}
              placeholder="Search address or drag marker on map"
            />
            <div className="flex items-center gap-3 text-xs text-muted mt-1">
              <span>Lat: {typeof editingPOI.location?.[1] === 'number' ? editingPOI.location[1].toFixed(5) : '—'}</span>
              <span>Lng: {typeof editingPOI.location?.[0] === 'number' ? editingPOI.location[0].toFixed(5) : '—'}</span>
              <button
                onClick={() => {
                  if (editingPOI.location?.[0] && map) {
                    flyToAndShowPin(
                      editingPOI.location[1],
                      editingPOI.location[0],
                      null,
                      map,
                      postMessageToPopMapWindow
                    );
                  }
                }}
                className="text-interactive hover:underline ml-auto"
              >
                Go to Location
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-border">
          <Button variant="outline" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          {!poi.isNew && (
            <Button variant="destructive" size="sm" onClick={deletePOI}>
              Delete
            </Button>
          )}
          <Button variant="default" size="sm" onClick={updatePOI}>
            {poi.isNew ? 'Complete' : 'Update'}
          </Button>
        </div>
      </div>
    );
  }

  // View Mode
  return (
    <div className="flex items-start gap-3 py-2 px-3 rounded-lg border border-border bg-subtle">
      {/* Checkbox */}
      <div className="pt-1">
        <Checkbox
          checked={checkedPoiIds.includes(poi.id)}
          onCheckedChange={isChecked => {
            setCheckedPoiIds(prev => (isChecked ? [...prev, poi.id] : prev.filter(id => id !== poi.id)));
          }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="font-medium text-sm truncate">{poi.displayName}</span>
          {severityOption && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${severityOption.dotColor} text-white`}>
              {severityOption.label}
            </span>
          )}
        </div>
        <div className="text-xs text-muted space-y-1">
          {poi.description && <p className="truncate">{poi.description}</p>}
          {editingPOI.address && <p className="truncate">{editingPOI.address}</p>}
          <p>
            Lat: {typeof poi.location?.[1] === 'number' ? poi.location[1].toFixed(3) : '—'} · Lng:{' '}
            {typeof poi.location?.[0] === 'number' ? poi.location[0].toFixed(3) : '—'}
          </p>
          <p>
            {poi.createdBy || '—'} ·{' '}
            {poi.creationTimestamp ? new Date(poi.creationTimestamp * 1000).toLocaleDateString() : '—'}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 pt-0.5">
        {setEditablePoiIds != null && (
          <button
            onClick={enableEdit}
            className="p-1.5 hover:bg-strong rounded transition-colors"
            aria-label="Edit POI"
          >
            <Pencil size={16} className="text-muted hover:text-interactive" />
          </button>
        )}
        <button onClick={flyTo} className="p-1.5 hover:bg-strong rounded transition-colors" aria-label="Go to POI">
          <MapPin size={16} className="text-muted hover:text-interactive" />
        </button>
      </div>
    </div>
  );
}
